import {
  ReferencePage,
  Entity,
  EntityLists,
  QuickSelect,
  LetterState,
} from '@squareup/dex-tech-ui';
import { ReferenceHrefBuilders, toUrl } from '@squareup/dex-tech-utils-routing';
import { Head } from '@squareup/dex-ui';
import { Markdown } from '@squareup/dex-ui-markdown';
import { Box } from '@squareup/dex-ui-shared-base';
import { getNextPublicSquareDocsURL } from '@squareup/dex-utils-environment';
import { areEntityListsEqual } from '@squareup/dex-utils-oas';
import { useHash } from '@squareup/dex-utils-shared-routing-hooks';
import { stripV1 } from '@squareup/dex-utils-text';
import React, { useCallback, useMemo, useRef } from 'react';
import { shallowEqual } from 'react-redux';

interface EntityPageContentProps {
  type: 'object' | 'enum';
  entities: Entity[] | undefined;
  title: string;
  description: string;
  currentVersion: string;
}

const ALPHABET = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'];

/**
 * Adds the given entity to the map indexed by the first letter of its name.
 */
function addEntityByNameFirstLetterToMap(
  map: Map<string, Entity[]>,
  entity: Entity
) {
  const firstLetter = stripV1(entity.name).charAt(0);
  let letterList = map.get(firstLetter);

  if (!letterList) {
    letterList = [];
    map.set(firstLetter, letterList);
  }

  letterList.push(entity);

  return map;
}

/**
 * Takes a map of letter to entities and sorts the map keys as well as the entities in each bucket.
 */
function getSortedLetterEntitiesMap(
  lettersEntitiesMap: Map<string, Entity[]>
): Map<string, Entity[]> {
  const sortedLettersEntitiesMap = new Map();
  [...lettersEntitiesMap.keys()].sort().forEach((letter) => {
    const sortedEntities = lettersEntitiesMap.get(letter)?.sort((a, b) => {
      const rootNameA = stripV1(a.name);
      const rootNameB = stripV1(b.name);

      if (rootNameA === rootNameB) {
        return a.isV1 ? 1 : -1;
      } else {
        return rootNameB.toLowerCase() < rootNameA.toLowerCase() ? 1 : -1;
      }
    });

    sortedLettersEntitiesMap.set(letter, sortedEntities);
  });

  return sortedLettersEntitiesMap;
}

const ActiveQuickSelect = ({
  letterStates,
  type,
  getListElement,
}: {
  letterStates: LetterState[] | undefined;
  type: EntityPageContentProps['type'];
  getListElement: () => HTMLElement | null;
}) => {
  const hashParams = useHash();
  // Grab the first key after the hash, note we are not after a value but an anchor ex. #object-index-F
  const hash = hashParams.keys().next().value;

  return letterStates ? (
    <QuickSelect
      getListElement={getListElement}
      type={type}
      selectedIndex={hash}
      letterStates={letterStates}
    />
  ) : null;
};

const EntityPageContent = React.memo(
  ({
    entities,
    type,
    title,
    description,
    currentVersion,
  }: EntityPageContentProps) => {
    const entitiyListRef = useRef<HTMLElement | null>(null);

    const [letterStates, sortedLettersEntitiesMap] = useMemo(() => {
      if (!entities) {
        return [];
      }

      const lettersEntitiesMap = entities.reduce(
        addEntityByNameFirstLetterToMap,
        new Map()
      );

      const sortedLettersEntitiesMap =
        getSortedLetterEntitiesMap(lettersEntitiesMap);

      const usedLettersSet = new Set(sortedLettersEntitiesMap.keys());

      return [
        ALPHABET.map((letter) => ({
          letter,
          disabled: !usedLettersSet.has(letter),
        })),
        sortedLettersEntitiesMap,
      ];
    }, [entities]);

    const setEntityListRef = useCallback((ref: HTMLElement | null) => {
      entitiyListRef.current = ref;
    }, []);

    const getEntityListRef = useCallback(() => {
      return entitiyListRef.current;
    }, []);

    const aside = useMemo(() => {
      return {
        trackingId: 'objects-enums',
        content: sortedLettersEntitiesMap ? (
          <EntityLists
            type={type}
            lettersEntitiesMap={sortedLettersEntitiesMap}
            setListRef={setEntityListRef}
            currentVersion={currentVersion}
          />
        ) : null,
        navigationItems: [
          {
            trackingId: 'navigate-to-reference-objects',
            isActive: type === 'object',
            testId: 'objects',
            text: 'Objects',
            href: ReferenceHrefBuilders.versionObjects({
              square_version: currentVersion,
            }),
          },
          {
            trackingId: 'navigate-to-reference-enums',
            isActive: type === 'enum',
            testId: 'enums',
            text: 'Enums',
            href: ReferenceHrefBuilders.versionEnums({
              square_version: currentVersion,
            }),
          },
        ],
      };
    }, [sortedLettersEntitiesMap, setEntityListRef, type, currentVersion]);

    const headCanonicalLinkFn =
      type === 'object'
        ? ReferenceHrefBuilders.versionObjects
        : ReferenceHrefBuilders.versionEnums;

    return (
      <>
        <Head
          description={description}
          preconnectUrl={getNextPublicSquareDocsURL()}
          title={title}
          canonicalPath={toUrl(
            headCanonicalLinkFn({
              square_version: currentVersion,
            })
          )}
        ></Head>
        <ReferencePage
          eyebrow={'Square API'}
          title="Objects &amp; Enums"
          subTitle="Reference for data types shared across the Square API"
          description={
            <>
              <Box margin={{ bottom: '2x' }}>
                <Markdown
                  markdown={`The Square API shares common objects across endpoints, such as
                  \`Address\`or \`Payment\`, to make it
                  easier for you to build your app and enforce consistency
                  across our API surface. surface. This reference documents
                  every common object currently in use.`}
                />
              </Box>
              {letterStates ? (
                <ActiveQuickSelect
                  getListElement={getEntityListRef}
                  type={type}
                  letterStates={letterStates}
                />
              ) : null}
            </>
          }
          aside={aside}
        />
      </>
    );
  },
  (prevProps, nextProps) => {
    const { entities: prevPropEntities, ...prevPropsRest } = prevProps;
    const { entities: nextPropsEntities, ...nextPropsRest } = nextProps;

    if (!shallowEqual(prevPropsRest, nextPropsRest)) {
      return false;
    }

    // Entities aren't stable, but their value doesn't actually change.
    // So doing an equality check on them otherwise the rerender cost is high
    return areEntityListsEqual(prevPropEntities, nextPropsEntities);
  }
);

EntityPageContent.displayName = 'EntityPageContent';

export { EntityPageContent };
